import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Check, X, ArrowRight, Sparkles, Server, ShieldCheck, BarChart3,
  PhoneCall, Globe, Cpu, Layers, Banknote, Trophy
} from "lucide-react";
import LiveReaders from "@/components/marketing/LiveReaders";
import CodeRain from "@/components/marketing/CodeRain";
import { useCodeRain } from "@/lib/siteEffects";

/**
 * /why-connexis — competitive landing page positioning Connexis vs
 * LeadProsper, LeadByte, boberdoo, ActiveProspect/LeadConduit.
 *
 * Goals: SEO ("connexis vs leadprosper" etc), close prospects mid-eval,
 * surface our defensible differentiators (FCA-vertical AI, ping tree,
 * voice + chat + dashboards in one).
 */

const COMPETITORS = [
  { name: "Connexis",    short: "Connexis",    accent: true,  monthly: "£99–£249/mo", per_lead: "10% routing margin", region: "UK · FCA-vertical" },
  { name: "LeadProsper", short: "LeadProsper", accent: false, monthly: "$500/mo (~£395)", per_lead: "$0.05–$0.0005/lead",  region: "US · sector-agnostic" },
  { name: "LeadByte",    short: "LeadByte",    accent: false, monthly: "£500/mo",         per_lead: "Custom · volume-based", region: "UK · sector-agnostic" },
  { name: "boberdoo",    short: "boberdoo",    accent: false, monthly: "$500–$3,000/mo",  per_lead: "Bundled + LeadQC $0.02–$0.20", region: "US · sector-agnostic" },
  { name: "ActiveProspect", short: "LeadConduit", accent: false, monthly: "$24K+/yr (or $10 PAYG)", per_lead: "$0.15/cert",          region: "US · validation focus" },
];

const COMPARISON_ROWS = [
  { feature: "FCA-vertical specialization (UK mortgages, debt, life, pensions)", Icon: Globe,        connexis: true,  leadprosper: false, leadbyte: false, boberdoo: false, activeprospect: false },
  { feature: "AI lead qualification — built-in, every sector",                   Icon: Cpu,          connexis: true,  leadprosper: false, leadbyte: false, boberdoo: false, activeprospect: false },
  { feature: "Outbound AI voice agent (sub-300ms · FCA-aware scripts)",          Icon: PhoneCall,    connexis: true,  leadprosper: false, leadbyte: false, boberdoo: false, activeprospect: false },
  { feature: "Real-time fixed-price routing engine",                            Icon: Layers,       connexis: true,  leadprosper: true,  leadbyte: true,  boberdoo: true,  activeprospect: false },
  { feature: "Buyer-facing dashboards (self-serve, not admin-only)",             Icon: BarChart3,    connexis: true,  leadprosper: false, leadbyte: "partial", boberdoo: false, activeprospect: false },
  { feature: "AML / sanctions screening built-in",                               Icon: ShieldCheck,  connexis: true,  leadprosper: false, leadbyte: false, boberdoo: false, activeprospect: "partial" },
  { feature: "Public partner / affiliate leaderboard",                           Icon: Trophy,       connexis: true,  leadprosper: false, leadbyte: false, boberdoo: false, activeprospect: false },
  { feature: "TCPA / consent capture (TrustedForm-style)",                       Icon: ShieldCheck,  connexis: true,  leadprosper: true,  leadbyte: true,  boberdoo: true,  activeprospect: true },
  { feature: "Stripe billing + Connect Express payouts",                         Icon: Banknote,     connexis: true,  leadprosper: false, leadbyte: false, boberdoo: false, activeprospect: false },
  { feature: "GBP-native pricing, no currency conversion",                       Icon: Banknote,     connexis: true,  leadprosper: false, leadbyte: true,  boberdoo: false, activeprospect: false },
  { feature: "Webhook + Direct-Post + Polling delivery",                         Icon: Server,       connexis: true,  leadprosper: true,  leadbyte: true,  boberdoo: true,  activeprospect: true },
];

const Cell = ({ v }) => {
  if (v === true)        return <Check className="size-4 text-[#0055FF] mx-auto" strokeWidth={2.4} />;
  if (v === false)       return <X className="size-4 text-slate-300 mx-auto" strokeWidth={2.2} />;
  if (v === "partial")   return <span className="text-[10.5px] uppercase tracking-widest text-slate-500 font-mono mx-auto block text-center">partial</span>;
  return <span className="text-xs">{v}</span>;
};

export default function WhyConnexis() {
  const rain = useCodeRain("why_connexis");
  return (
    <div className="bg-white">
      {/* HERO */}
      <section className="relative border-b border-slate-200 overflow-hidden">
        <div className="absolute inset-0 dot-grid dot-grid-fade opacity-70" />
        {rain.on && <CodeRain variant="light" intensity={rain.intensity} />}
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#0055FF]">Why Connexis</p>
          <h1 className="mt-3 text-5xl sm:text-6xl tracking-[-0.04em] font-semibold text-slate-900 max-w-4xl leading-[1.04]">
            Built for UK fintech.<br />
            <span className="text-[#0055FF]">Priced for operators.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-slate-600">
            See how Connexis stacks up against LeadProsper, LeadByte, boberdoo and ActiveProspect on price, features and what's actually built for UK financial services.
          </p>
          <div className="mt-8 flex gap-3 flex-wrap">
            <Link to="/pricing"><Button className="rounded-full h-11 px-5 bg-[#0055FF] hover:bg-[#0044CC]" data-testid="why-cta-pricing">See pricing <ArrowRight className="size-4" /></Button></Link>
            <Link to="/book-demo"><Button variant="outline" className="rounded-full h-11 px-5" data-testid="why-cta-demo">Book a demo</Button></Link>
          </div>
        </div>
      </section>

      {/* PRICING-AT-A-GLANCE */}
      <section className="py-16 bg-slate-50/60 border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-[#0055FF] mb-4">
            <Banknote className="size-4" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em]">Pricing · at a glance</span>
          </div>
          <h2 className="text-3xl sm:text-4xl tracking-[-0.02em] font-medium text-slate-900 max-w-3xl">Within the range of LeadProsper. With the breadth of a fintech operating system.</h2>

          <div className="mt-10 grid lg:grid-cols-5 gap-4">
            {COMPETITORS.map((c) => (
              <div
                key={c.name}
                className={`relative rounded-2xl border p-5 ${c.accent ? "border-[#0055FF] bg-white shadow-float" : "border-slate-200 bg-white"}`}
                data-testid={`compare-tile-${c.short.toLowerCase()}`}
              >
                {c.accent && <span className="absolute -top-2 right-4 rounded-full bg-[#0055FF] text-white text-[9px] font-semibold px-2 py-0.5 tracking-wider uppercase">Recommended</span>}
                <div className={`text-[11px] uppercase tracking-[0.16em] font-semibold ${c.accent ? "text-[#0055FF]" : "text-slate-500"}`}>{c.short}</div>
                <div className="mt-3">
                  <div className="text-[11px] uppercase tracking-widest text-slate-500 font-mono">Monthly</div>
                  <div className="mt-0.5 text-[15px] font-semibold text-slate-900 tabular-nums">{c.monthly}</div>
                </div>
                <div className="mt-3">
                  <div className="text-[11px] uppercase tracking-widest text-slate-500 font-mono">Per-lead</div>
                  <div className="mt-0.5 text-[13px] text-slate-700 tabular-nums">{c.per_lead}</div>
                </div>
                <div className="mt-3 text-[11.5px] text-slate-500">{c.region}</div>
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs text-slate-500 max-w-3xl">
            Pricing data sources: official platform pricing pages + G2/Capterra/SoftwareSuggest verified listings as of Feb 2026.
            Currency conversion at £1 = $1.265.
          </p>
        </div>
      </section>

      {/* FEATURE MATRIX */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-[#0055FF] mb-4">
            <Sparkles className="size-4" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em]">Feature comparison</span>
          </div>
          <h2 className="text-3xl sm:text-4xl tracking-[-0.02em] font-medium text-slate-900">What you get by default — vs. what they charge extra for.</h2>

          <div className="mt-10 rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="why-feature-matrix">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50/80 border-b border-slate-200">
                    <th className="text-left text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold p-4 w-[40%]">Capability</th>
                    <th className="text-center text-[11px] uppercase tracking-[0.16em] text-[#0055FF] font-semibold p-4">Connexis</th>
                    <th className="text-center text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold p-4">LeadProsper</th>
                    <th className="text-center text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold p-4">LeadByte</th>
                    <th className="text-center text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold p-4">boberdoo</th>
                    <th className="text-center text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold p-4">LeadConduit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {COMPARISON_ROWS.map((r, i) => (
                    <tr key={i} className="hover:bg-slate-50/40 transition-colors" data-testid={`compare-row-${i}`}>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <span className="inline-grid place-items-center size-7 rounded-md bg-[#0F172A] text-white shrink-0">
                            <r.Icon className="size-3.5" strokeWidth={1.7} />
                          </span>
                          <span className="text-[13.5px] text-slate-900">{r.feature}</span>
                        </div>
                      </td>
                      <td className="p-4"><Cell v={r.connexis} /></td>
                      <td className="p-4"><Cell v={r.leadprosper} /></td>
                      <td className="p-4"><Cell v={r.leadbyte} /></td>
                      <td className="p-4"><Cell v={r.boberdoo} /></td>
                      <td className="p-4"><Cell v={r.activeprospect} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* MIGRATION PROMISE */}
      <section className="py-20 bg-slate-50/60 border-y border-slate-200">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 text-[#0055FF] mb-4">
            <Sparkles className="size-4" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em]">Migration promise</span>
          </div>
          <h2 className="text-3xl sm:text-4xl tracking-[-0.02em] font-medium text-slate-900">Already on LeadProsper or LeadByte?</h2>
          <p className="mt-4 text-slate-600 text-[15px] max-w-2xl mx-auto">
            We'll match your current monthly platform fee for the first 90 days while we replicate your buyer accounts, ping trees and webhook integrations.
            Our team handles the migration — you keep routing leads without missing a delivery.
          </p>
          <div className="mt-8">
            <Link to="/book-demo">
              <Button className="rounded-full h-11 px-5 bg-[#0055FF] hover:bg-[#0044CC]" data-testid="why-migration-cta">
                Talk to migration team <ArrowRight className="size-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-3xl sm:text-4xl tracking-[-0.02em] font-medium text-slate-900">Built for UK fintech operators who want the whole stack — not a routing tool.</h3>
          <div className="mt-7 flex gap-3 justify-center flex-wrap">
            <Link to="/pricing"><Button className="rounded-full h-11 px-5 bg-[#0055FF] hover:bg-[#0044CC]" data-testid="why-final-pricing">See pricing</Button></Link>
            <Link to="/book-demo"><Button variant="outline" className="rounded-full h-11 px-5" data-testid="why-final-demo">Book a demo</Button></Link>
          </div>
        </div>
      </section>

      <LiveReaders page="why-connexis" />
    </div>
  );
}
